import type { monaco } from 'react-monaco-editor';
import { editor } from 'monaco-types';
import { builtinFunctions } from './pantherflow_funcs';
import { pfAutocomplete, pfHover, pfInlayHints, pfSignatureHelp } from './wasmBridge';

const operators = [
  'datatable',
  'extend',
  'join',
  'sort',
  'project',
  'search',
  'summarize',
  'limit',
  'union',
  'visualize',
  'where',
  'range',
];

const keywords = [
  'by',
  'on',
  'in',
  'and',
  'or',
  'not',
  'let',
  'between',
  'fn',

  'asc',
  'desc',

  'nulls',
  'first',
  'last',

  '$right',
  '$left',

  'from',
  'to',
  'step',
];

const constants = ['true', 'false', 'null'];

// A word is only treated as a function call when it is immediately followed by
// an opening parenthesis (optionally separated by whitespace). PantherFlow
// function names like `count`, `sum`, `len`, and `now` are common field names
// and aliases too, so highlighting every occurrence of them as a function call
// produced false positives — e.g. the alias in `summarize count = agg.count()`
// was highlighted even though only the `agg.count(` usage is a real call
// (EPD-1423). The `(?=\s*\()` lookahead restricts the function-call styling to
// genuine call sites while leaving bare identifiers as plain identifiers.
export const functionCallPattern = /[\w@#\-$]+(?=\s*\()/;

// Examples and documentation available at:
// https://microsoft.github.io/monaco-editor/monarch.html
export const pantherflowConfig: monaco.languages.IMonarchLanguage = {
  name: 'pantherflow',
  mimeTypes: ['text/pantherflow'],
  displayName: 'PantherFlow',
  defaultToken: 'identifier',
  brackets: [
    ['{', '}', 'delimiter.curly'],
    ['[', ']', 'delimiter.square'],
    ['(', ')', 'delimiter.parenthesis'],
  ] as unknown as monaco.languages.IMonarchLanguageBracket[], // type signature is wrong

  operators,
  keywords,
  builtinFunctions: builtinFunctions.map(func => func.split('.').slice(-1)[0]),
  stringEscapes: /\\(?:['"\\bfnrt]|[0-3][0-7][0-7]|x[0-9a-fA-F]{2}|u[0-9a-fA-F]{4})/,
  constants,

  tokenizer: {
    root: [
      { include: '@whitespace' },
      { include: '@literals' },
      { include: '@numbers' },
      { include: '@comments' },

      // These find non-terminated strings, they don't cover all possibilities
      // but this doesn't have to be perfect
      [/'([^'\\]|\\.)*$/, 'string.invalid'],
      [/"([^"\\]|\\.)*$/, 'string.invalid'],

      [/'/, 'string', '@string'],
      [/"/, 'string', '@dstring'],

      [/[;,.]/, 'delimiter'],
      [/[()[\]{}]/, '@brackets'],
      [/[<>=!%&+\-*/|~^]/, 'operator'],
      // A builtin function name is only styled as a function call when it is
      // immediately followed by '(' (EPD-1423). Keywords/operators are still
      // resolved here first so e.g. `not(` keeps its keyword styling.
      [
        functionCallPattern,
        {
          cases: {
            '@keywords': 'keyword',
            '@constants': 'keyword',
            '@operators': 'operator.sql',
            '@builtinFunctions': 'meta.function-call',
            '@default': 'identifier',
          },
        },
      ],
      [
        /[\w@#\-$]+/,
        {
          cases: {
            '@keywords': 'keyword',
            '@constants': 'keyword',
            '@operators': 'operator.sql',
            '@default': 'identifier',
          },
        },
      ],
    ],
    whitespace: [[/\s+/, 'white']],
    comments: [['\\/\\/+.*', 'comment']],
    numbers: [
      [/0[xX][0-9a-fA-F]*/, 'number.hex'],
      [/[+-]?\d+\.\d*/, 'number.float'],
      [/[+-]?\d+/, 'number'],
    ],
    string: [
      [/[^\\']+/, 'string'],
      [/@stringEscapes/, 'string.escape'],
      [/\\./, 'string.escape.invalid'],
      [/'/, 'string', '@pop'],
    ],
    dstring: [
      [/[^\\"]+/, 'string'],
      [/@stringEscapes/, 'string.escape'],
      [/\\./, 'string.escape.invalid'],
      [/"/, 'string', '@pop'],
    ],

    literals: [
      [/\d{4}-\d{2}-\d{2}(\s+\d{2}:\d{2}(:\d{2}(\.\d{0,3})?)?)?/, 'number'],
      [/\d+(tick|microsecond|ms|s|m|h|d)/, 'number'],
    ],
  },
};

export const registerPantherFlowCompletions = (monacoInstance: typeof monaco) => {
  return monacoInstance.languages.registerCompletionItemProvider('pantherflow', {
    triggerCharacters: ['.', ' '],
    provideCompletionItems(
      model: editor.ITextModel,
      position: monaco.Position,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _context: monaco.languages.CompletionContext,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _token: monaco.CancellationToken,
    ) {
      const comp = pfAutocomplete(model.getValue(), {
        line: position.lineNumber,
        column: position.column,
      });

      return {
        suggestions: [
          ...comp.completionItems.map(item => ({
            label: item.label,
            detail: item.detail,
            kind: item.kind,
            insertText: item.insertText,
            documentation: item.documentation,
          })),
        ] as monaco.languages.CompletionItem[],
      };
    },
  });
};

export const registerPantherFlowInlayHints = (monacoInstance: typeof monaco) => {
  // this provider is called whenever a user scrolls, even if the text has not changed.
  // caching the values avoids stutters in the editor
  let lastHints = [] as monaco.languages.InlayHint[];
  let lastModel = '';

  return monacoInstance.languages.registerInlayHintsProvider('pantherflow', {
    provideInlayHints: (model: editor.ITextModel) => {
      // if model.getValue() is the same return previous hints
      if (lastModel === model.getValue()) {
        return {
          hints: lastHints,
          dispose: () => {},
        };
      }
      lastModel = model.getValue();
      lastHints = pfInlayHints(model.getValue()).inlayHints.map(hint => ({
        label: hint.label,
        position: { lineNumber: hint.position.line, column: hint.position.column },
        kind: hint.kind,
        paddingRight: true, // always add padding here for looks
      }));

      return {
        hints: lastHints,
        dispose: () => {},
      };
    },
  });
};

export const registerHover = (monacoInstance: typeof monaco) => {
  return monacoInstance.languages.registerHoverProvider('pantherflow', {
    provideHover(
      model: editor.ITextModel,
      position: monaco.Position,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _token: monaco.CancellationToken,
    ) {
      const hover = pfHover(model.getValue(), {
        line: position.lineNumber,
        column: position.column,
      });

      return hover as monaco.languages.Hover;
    },
  });
};

export const registerPantherFlowSignatureHelp = (monacoInstance: typeof monaco) => {
  return monacoInstance.languages.registerSignatureHelpProvider('pantherflow', {
    signatureHelpTriggerCharacters: ['(', ','],
    signatureHelpRetriggerCharacters: [','],
    provideSignatureHelp: (model: editor.ITextModel, position: monaco.Position) => {
      return {
        value: pfSignatureHelp(model.getValue(), {
          line: position.lineNumber,
          column: position.column,
        }),
        dispose: () => {},
      };
    },
  });
};

export const languageConfiguration = {
  comments: {
    lineComment: '//',
  },
  brackets: [
    ['{', '}'],
    ['[', ']'],
    ['(', ')'],
  ],
  autoClosingPairs: [
    { open: '(', close: ')', notIn: ['string', 'comment'] },
    { open: '{', close: '}', notIn: ['string', 'comment'] },
    { open: '[', close: ']', notIn: ['string', 'comment'] },
    { open: "'", close: "'", notIn: ['string', 'comment'] },
    { open: '"', close: '"', notIn: ['string', 'comment'] },
  ],
  surroundingPairs: [
    { open: '(', close: ')' },
    { open: '{', close: '}' },
    { open: '[', close: ']' },
    { open: "'", close: "'" },
    { open: '"', close: '"' },
  ],
} as monaco.languages.LanguageConfiguration;

export default registerPantherFlowCompletions;
